import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchArticleDetail, fetchArticleList } from "../../utils/articleApi";
import { ArticleDetail, ArticleSummary } from "../../components/article/types";
import ArticleTopBar from "../../components/article/topBar";
import ArticleHeading from "../../components/article/heading";
import ArticleDescription from "../../components/article/description";
import ArticleContent from "../../components/article/content";
import RelatedArticles from "../../components/article/relatedArticles";
import "../../components/article/style.css";
import Intro from "../../components/intro";

const emptyListResult = {
  articles: [] as ArticleSummary[],
  nextHit: false,
  pageNo: 1,
};

const mergeArticles = (
  existing: ArticleSummary[],
  incoming: ArticleSummary[],
  currentId?: string
) => {
  const seen = new Set(existing.map((item) => item._id));
  const next = incoming.filter((item) => {
    if (currentId && item._id === currentId) return false;
    if (seen.has(item._id)) return false;
    seen.add(item._id);
    return true;
  });
  return [...existing, ...next];
};

const Article = () => {
  const { id: articleId } = useParams();
  const [article, setArticle] = useState<ArticleDetail | null>(null);
  const [related, setRelated] = useState<ArticleSummary[]>([]);
  const [visibleCount, setVisibleCount] = useState(2);
  const [pageNo, setPageNo] = useState(1);
  const [nextHit, setNextHit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!articleId) return;

    let cancelled = false;

    const loadArticle = async () => {
      setLoading(true);
      setError("");
      setVisibleCount(2);
      setRelated([]);
      setPageNo(1);
      setNextHit(false);
      try {
        const [detail, list] = await Promise.all([
          fetchArticleDetail(articleId),
          fetchArticleList(1).catch(() => emptyListResult),
        ]);
        if (cancelled) return;
        setArticle(detail);
        setRelated(mergeArticles([], list.articles, articleId));
        setNextHit(list.nextHit);
        setPageNo(list.pageNo || 1);
      } catch (err) {
        if (cancelled) return;
        setArticle(null);
        setError(err instanceof Error ? err.message : "Something went wrong");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadArticle();

    return () => {
      cancelled = true;
    };
  }, [articleId]);

  const handleViewAll = () => {
    setVisibleCount(4);
  };

  const handleShowMore = async () => {
    if (loadingMore || !nextHit) return;
    setLoadingMore(true);
    try {
      const nextPage = pageNo + 1;
      const list = await fetchArticleList(nextPage);
      const merged = mergeArticles(related, list.articles, articleId);
      setRelated(merged);
      setNextHit(list.nextHit);
      setPageNo(list.pageNo || nextPage);
      setVisibleCount(merged.length);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMore(false);
    }
  };

  const visibleRelated = related.slice(0, visibleCount);

  return (
    <div className="article-page">
      <Intro />
      <ArticleTopBar label={article?.tags?.[0]} />
      <div className="article-page-inner">
        {loading && <div className="article-status">Loading article...</div>}
        {!loading && error && !article && (
          <div className="article-status article-status-error">{error}</div>
        )}
        {!loading && article && (
          <>
            <ArticleHeading article={article} />
            <ArticleDescription description={article.description} />
            <ArticleContent content={article.content} />
            <RelatedArticles
              articles={visibleRelated}
              showViewAll={related.length > 2 && visibleCount === 2}
              onViewAll={handleViewAll}
              showMore={nextHit && (visibleCount >= 4 || related.length <= 2)}
              onShowMore={handleShowMore}
              loadingMore={loadingMore}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default Article;
